/**
 * sfc32 — pinned as part of the replay contract.
 *
 * Integer-only: Math.imul and >>>0 throughout, never float arithmetic.
 * An LCG built on float multiply drifts into non-integer territory and
 * desyncs; that is the failure this module exists to avoid.
 *
 * State lives in the caller's Uint32Array so the sim allocates nothing:
 * five streams x four words, addressed by `stream * 4`.
 */
import { STREAM_COUNT, STREAM_NAMES } from './constants.js';

export const WORDS_PER_STREAM = 4;

/**
 * FNV-1a, exported so `scripts/build-config.ts`'s `configHash` uses this
 * SAME primitive instead of its own copy (AUDIT A-15) -- two
 * implementations of one hash is exactly how one gets silently upgraded
 * (a constant tweaked "for better distribution") while the other stays
 * pinned, and nothing would notice until a replay or a config hash
 * disagreed for no visible reason.
 */
export function fnv1a32(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h >>> 0;
}

/**
 * Each stream is seeded from hash(seed, streamName), so streams are
 * independent: adding a draw to one never shifts the values another sees.
 */
export function seedStreams(rng: Uint32Array, seed: number): void {
  for (let s = 0; s < STREAM_COUNT; s++) {
    const base = s * WORDS_PER_STREAM;
    const h = fnv1a32(`${seed >>> 0}:${STREAM_NAMES[s]}`);
    rng[base] = (h ^ 0x9e3779b9) >>> 0;
    rng[base + 1] = (Math.imul(h, 0x85ebca6b) ^ 0xc2b2ae35) >>> 0;
    rng[base + 2] = (Math.imul(h ^ 0x27d4eb2f, 0x165667b1)) >>> 0;
    rng[base + 3] = (h + 0x6d2b79f5) >>> 0;
    // Discard the first few outputs so low-entropy seeds decorrelate.
    for (let i = 0; i < 12; i++) nextU32(rng, s);
  }
}

/** Raw sfc32 step. Returns a uint32. */
export function nextU32(rng: Uint32Array, stream: number): number {
  const b = stream * WORDS_PER_STREAM;
  const a = rng[b], bb = rng[b + 1], c = rng[b + 2], d = rng[b + 3];
  const t = (a + bb + d) >>> 0;
  rng[b] = (bb ^ (bb >>> 9)) >>> 0;
  rng[b + 1] = (c + (c << 3)) >>> 0;
  rng[b + 2] = (((c << 21) | (c >>> 11)) + t) >>> 0;
  rng[b + 3] = (d + 1) >>> 0;
  return t;
}

/** Float in [0, 1). */
export function nextFloat(rng: Uint32Array, stream: number): number {
  return nextU32(rng, stream) / 4294967296;
}

/** Integer in [0, n). Rejection-free modulo is fine at these magnitudes. */
export function nextInt(rng: Uint32Array, stream: number, n: number): number {
  return n <= 1 ? 0 : nextU32(rng, stream) % n;
}

/** Float in [lo, hi). */
export function nextRange(rng: Uint32Array, stream: number, lo: number, hi: number): number {
  return lo + nextFloat(rng, stream) * (hi - lo);
}
